import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const EPL_COLOR = 'rgba(54, 162, 235, 1)';
const TCH_COLOR = 'rgba(255, 99, 132, 1)';
const EPL_LIGHT = 'rgba(54, 162, 235, 0.2)';
const TCH_LIGHT = 'rgba(255, 99, 132, 0.2)';

const DAY_WISE_TIME_SLOTS = (() => {
  const slots = [];
  const dayMinutes = 24 * 60;
  const startAbs = 12 * 60;
  const endAbs = dayMinutes + 11 * 60;

  for (let abs = startAbs; abs <= endAbs; abs += 30) {
    const clock = ((abs % dayMinutes) + dayMinutes) % dayMinutes;
    const hour = Math.floor(clock / 60);
    const minute = clock % 60;
    const isSecondHalf = minute >= 30;
    if (hour === 12) {
      slots.push(isSecondHalf ? '12:30 PM' : '12:00 PM');
    } else if (hour === 0) {
      slots.push(isSecondHalf ? '12:30 AM' : '12:00 AM');
    } else if (hour > 12) {
      slots.push(isSecondHalf ? `${hour - 12}:30 PM` : `${hour - 12}:00 PM`);
    } else {
      slots.push(isSecondHalf ? `${hour}:30 AM` : `${hour}:00 AM`);
    }
  }
  return slots;
})();

const getTimeSlotKey = (momentTime) => {
  const hour = momentTime.hour();
  const minute = momentTime.minute();
  const isSecondHalf = minute >= 30;

  if (hour === 9 && minute >= 30) return '9:30 AM';
  if (hour === 6 && minute <= 30) return isSecondHalf ? '6:30 AM' : '6:00 AM';
  if (hour === 12) return isSecondHalf ? '12:30 PM' : '12:00 PM';
  if (hour === 0) return isSecondHalf ? '12:30 AM' : '12:00 AM';
  if (hour > 12) return isSecondHalf ? `${hour - 12}:30 PM` : `${hour - 12}:00 PM`;
  return isSecondHalf ? `${hour}:30 AM` : `${hour}:00 AM`;
};

export const buildDayWiseChartData = (dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats) => {
  if (!dayData?.eplTchData) return null;

  const { EPL, Competitor } = dayData.eplTchData;
  const eplTimeData = {};
  const tchTimeData = {};

  EPL.labels.forEach((timestamp, index) => {
    if (isTournamentGame(EPL.game_name[index])) return;
    if (isExcludedFromDashboardStats(EPL.game_name[index])) return;
    const timeKey = getTimeSlotKey(parseTimestamp(timestamp));
    eplTimeData[timeKey] = (eplTimeData[timeKey] || 0) + EPL.tables[index];
  });

  Competitor.labels.forEach((timestamp, index) => {
    if (isTournamentGame(Competitor.game_name[index])) return;
    if (isExcludedFromDashboardStats(Competitor.game_name[index])) return;
    const timeKey = getTimeSlotKey(parseTimestamp(timestamp));
    tchTimeData[timeKey] = (tchTimeData[timeKey] || 0) + Competitor.tables[index];
  });

  return {
    labels: DAY_WISE_TIME_SLOTS,
    datasets: [
      {
        label: 'EPL',
        data: DAY_WISE_TIME_SLOTS.map((time) => eplTimeData[time] || 0),
        borderColor: EPL_COLOR,
        backgroundColor: EPL_LIGHT,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: 'TCH',
        data: DAY_WISE_TIME_SLOTS.map((time) => tchTimeData[time] || 0),
        borderColor: TCH_COLOR,
        backgroundColor: TCH_LIGHT,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };
};

export const computeDaySummary = (eplTchData, isTournamentGame, isExcludedFromDashboardStats) => {
  if (!eplTchData) return { epl: 0, tch: 0 };

  const sumSide = (source) => {
    let total = 0;
    let tournamentTotal = 0;
    source.game_name.forEach((game, index) => {
      if (isExcludedFromDashboardStats(game)) return;
      const count = source.tables[index];
      total += count;
      if (isTournamentGame(game)) tournamentTotal += count;
    });
    return total - tournamentTotal;
  };

  return {
    epl: sumSide(eplTchData.EPL),
    tch: sumSide(eplTchData.Competitor),
  };
};

const getDayWiseChartOptions = (title, { compact = true } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  transitions: { active: { animation: { duration: 0 } } },
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    title: {
      display: Boolean(title),
      text: title,
      font: { size: compact ? 11 : 16, weight: 'bold' },
      padding: compact ? 6 : 12,
    },
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: compact ? 6 : 14,
        font: { size: compact ? 9 : 13 },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.parsed.y} tables`,
      },
    },
  },
  scales: {
    x: {
      ticks: {
        font: { size: compact ? 6 : 10 },
        maxRotation: 45,
        minRotation: 45,
        autoSkip: !compact,
        maxTicksLimit: compact ? 16 : undefined,
      },
      grid: { display: !compact, color: 'rgba(0, 0, 0, 0.08)' },
    },
    y: {
      beginAtZero: true,
      ticks: {
        font: { size: compact ? 8 : 12 },
        stepSize: 1,
        precision: 0,
        callback: (value) => (Number.isInteger(value) ? value : undefined),
      },
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      title: {
        display: true,
        text: 'Tables',
        font: { size: compact ? 9 : 13, weight: 'bold' },
      },
    },
  },
});

const ChartSkeleton = () => (
  <div className="flex h-full flex-col justify-center gap-3 px-2">
    <div className="flex items-end justify-between gap-1 h-[140px]">
      {[35, 55, 40, 70, 45, 85, 50, 65, 42, 78].map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-md bg-gradient-to-t from-slate-200 to-slate-100 animate-pulse"
          style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
    <div className="mx-auto h-3 w-24 rounded-full bg-slate-200/80 animate-pulse" />
  </div>
);

const DayDetailModal = ({
  open,
  onClose,
  dayData,
  summary,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
}) => {
  const chartData = useMemo(() => {
    if (!dayData) return null;
    return buildDayWiseChartData(dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats);
  }, [dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats]);

  const chartOptions = useMemo(
    () => getDayWiseChartOptions(`Table Count by Time · ${dayData?.displayDate || ''}`, { compact: false }),
    [dayData?.displayDate]
  );

  const ChartComponent = chartType === 'line' ? Line : Bar;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[95vh] w-[95vw] max-w-6xl overflow-y-auto bg-white p-4 sm:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl font-bold text-slate-900 sm:text-2xl">
            {dayData?.displayDate || 'Day Details'}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Full operational day chart · 12:00 PM – 11:00 AM (next day)
          </p>
        </DialogHeader>

        {summary && (
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-center">
              <div className="text-sm font-medium text-blue-600">EPL Tables</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{summary.epl}</div>
            </div>
            <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-center">
              <div className="text-sm font-medium text-rose-600">TCH Tables</div>
              <div className="mt-1 text-2xl font-bold text-rose-700">{summary.tch}</div>
            </div>
            <div className="col-span-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-center sm:col-span-1">
              <div className="text-sm font-medium text-emerald-600">Difference</div>
              <div className="mt-1 text-2xl font-bold text-emerald-700">
                {summary.epl - summary.tch > 0 ? '+' : ''}
                {summary.epl - summary.tch}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 h-[380px] rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:h-[480px] sm:p-4">
          {dayData?.error ? (
            <div className="flex h-full items-center justify-center text-red-500">{dayData.error}</div>
          ) : chartData ? (
            <ChartComponent options={chartOptions} data={chartData} />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500">No data available</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DayWiseChartCard = React.memo(function DayWiseChartCard({
  dayData,
  summary,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
  onOpen,
}) {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    let idleId = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Defer chart mount slightly so scroll stays smooth
          idleId = window.setTimeout(() => setIsVisible(true), 40);
        } else {
          if (idleId) window.clearTimeout(idleId);
          setIsVisible(false);
        }
      },
      { rootMargin: '80px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => {
      if (idleId) window.clearTimeout(idleId);
      observer.disconnect();
    };
  }, []);

  const chartData = useMemo(() => {
    if (!isVisible) return null;
    return buildDayWiseChartData(dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats);
  }, [isVisible, dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats]);

  const chartOptions = useMemo(
    () => getDayWiseChartOptions(dayData.displayDate, { compact: true }),
    [dayData.displayDate]
  );

  const ChartComponent = chartType === 'line' ? Line : Bar;

  return (
    <div
      ref={containerRef}
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(dayData)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(dayData);
        }
      }}
      className="w-full cursor-pointer rounded-xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 [content-visibility:auto] [contain-intrinsic-size:360px]"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-800">{dayData.displayDate}</h3>
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600">
          View full
        </span>
      </div>

      <div className="h-[220px] sm:h-56">
        {dayData.error ? (
          <div className="flex h-full items-center justify-center text-sm text-red-500">{dayData.error}</div>
        ) : isVisible && chartData ? (
          <ChartComponent options={chartOptions} data={chartData} />
        ) : (
          <ChartSkeleton />
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-blue-50/80 px-2 py-2 text-center ring-1 ring-blue-100">
          <div className="font-semibold text-blue-600">EPL</div>
          <div className="text-lg font-bold text-blue-700">{summary.epl}</div>
        </div>
        <div className="rounded-lg bg-rose-50/80 px-2 py-2 text-center ring-1 ring-rose-100">
          <div className="font-semibold text-rose-600">TCH</div>
          <div className="text-lg font-bold text-rose-700">{summary.tch}</div>
        </div>
      </div>
    </div>
  );
});

export const DayWiseProcessingPanel = () => (
  <div className="flex h-48 items-center justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
  </div>
);

const DayWiseBreakdown = ({
  entries,
  loading,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
}) => {
  const [selectedDay, setSelectedDay] = useState(null);

  const entriesWithSummary = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        summary: computeDaySummary(entry.eplTchData, isTournamentGame, isExcludedFromDashboardStats),
      })),
    [entries, isTournamentGame, isExcludedFromDashboardStats]
  );

  if (loading) {
    return <DayWiseProcessingPanel />;
  }

  if (entriesWithSummary.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-slate-500">
        No day-wise data available for the selected range
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {entriesWithSummary.map((dayData) => (
          <DayWiseChartCard
            key={dayData.date}
            dayData={dayData}
            summary={dayData.summary}
            chartType={chartType}
            parseTimestamp={parseTimestamp}
            isTournamentGame={isTournamentGame}
            isExcludedFromDashboardStats={isExcludedFromDashboardStats}
            onOpen={setSelectedDay}
          />
        ))}
      </div>

      <DayDetailModal
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        dayData={selectedDay}
        summary={selectedDay?.summary}
        chartType={chartType}
        parseTimestamp={parseTimestamp}
        isTournamentGame={isTournamentGame}
        isExcludedFromDashboardStats={isExcludedFromDashboardStats}
      />
    </>
  );
};

export default DayWiseBreakdown;
